const asArray = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value.filter(Boolean);
    if (typeof value === 'object') return Object.values(value).filter(Boolean);

    return [];
};

export const normalizeItemsArray = asArray;

export const getNumber = (value, fallback = 0) => {
    const number = Number(value);

    return Number.isFinite(number) ? number : fallback;
};

export const getTextValue = (...values) => {
    const found = values.find((value) => {
        return value !== null && value !== undefined && String(value).trim() !== '';
    });

    return found !== undefined && found !== null ? String(found).trim() : '';
};

export const formatMoney = (value) => {
    const number = Number(value);

    if (!Number.isFinite(number) || number <= 0) return 'После подтверждения';

    return `${new Intl.NumberFormat('ru-RU', {
        maximumFractionDigits: 0,
    }).format(number)} ₽`;
};

export const formatWeight = (value) => {
    const number = Number(value);

    if (!Number.isFinite(number) || number <= 0) return '';

    const maximumFractionDigits = number >= 100 ? 0 : number >= 10 ? 1 : 2;

    return `${new Intl.NumberFormat('ru-RU', {
        maximumFractionDigits,
    }).format(number)} кг`;
};

export const getProductObject = (item = {}) => {
    if (item?.productId && typeof item.productId === 'object') return item.productId;
    if (item?.product && typeof item.product === 'object') return item.product;
    if (item?.itemProduct && typeof item.itemProduct === 'object') return item.itemProduct;

    return {};
};

export const getProductIdFromItem = (item = {}) => {
    const product = getProductObject(item);

    return getTextValue(
        item?.productId && typeof item.productId !== 'object' ? item.productId : '',
        item?.product && typeof item.product !== 'object' ? item.product : '',
        product?._id,
        product?.id,
        item?.product?._id,
        item?.calculatorData?.modelId,
        item?.productId?._id,
        item?.productId?.id,
        item?._productId
    );
};

export const getCartItemId = (item = {}) => {
    const productId = getProductIdFromItem(item);

    return getTextValue(
        item?.cartItemId,
        item?.cartItemKey,
        item?.lineItemId,
        item?._id && String(item._id) !== String(productId) ? item._id : '',
        item?.calculatorData?.variantKey,
        productId
    );
};

export const getOrderItemQuantity = (item = {}) => {
    return Math.max(1, getNumber(item?.purchasedQty ?? item?.qty ?? item?.quantity, 1));
};

export const getOrderItemCategoryLabel = (item = {}) => {
    const product = getProductObject(item);
    const calc = item?.calculatorData || {};

    return getTextValue(
        calc?.categoryLabel,
        calc?.categoryName,
        item?.categoryLabel,
        item?.categoryName,
        product?.categoryName,
        product?.categorySlug
    );
};

export const getOrderItemName = (item = {}) => {
    const product = getProductObject(item);
    const calc = item?.calculatorData || {};

    return getTextValue(
        calc?.modelName,
        item?.fullName,
        item?.name,
        product?.fullName,
        product?.name,
        product?.article,
        'Товар'
    );
};

export const getOrderItemImage = (item = {}) => {
    const product = getProductObject(item);
    const calc = item?.calculatorData || {};
    const pictures = Array.isArray(product?.productPictures) ? product.productPictures : [];
    const mainPicture = pictures.find((picture) => picture?.visibleImg && picture?.mainImg);
    const visiblePicture = pictures.find((picture) => picture?.visibleImg);
    const firstPicture = pictures[0];

    return getTextValue(
        calc?.mainImage,
        item?.mainImage,
        item?.reducedImage,
        item?.img,
        product?.reducedImage,
        product?.img,
        mainPicture?.img,
        visiblePicture?.img,
        firstPicture?.img
    );
};

export const getOrderItemSizeLabel = (item = {}) => {
    const calc = item?.calculatorData || {};

    return getTextValue(
        calc?.sizeLabel,
        calc?.selectedSizeLabel,
        calc?.millingDimensionsLabel,
        calc?.dimensionsLabel,
        item?.sizeLabel,
        item?.selectedSizeLabel,
        item?.millingDimensionsLabel,
        item?.dimensionsLabel,
        item?.selectedSize?.label
    );
};

export const getOrderItemSelectedSizeKey = (item = {}) => {
    const calc = item?.calculatorData || {};

    return getTextValue(
        calc?.selectedSizeKey,
        calc?.sizeKey,
        item?.selectedSizeKey,
        item?.sizeKey,
        getOrderItemSizeLabel(item)
    );
};

export const getOrderItemWeightKg = (item = {}) => {
    const calc = item?.calculatorData || {};

    return getNumber(
        calc?.weightKg ??
        calc?.selectedWeightKg ??
        calc?.blankWeightKg ??
        item?.weightKg ??
        item?.selectedWeightKg ??
        item?.blankWeightKg,
        0
    );
};

export const getOrderItemMaterialDensity = (item = {}) => {
    const calc = item?.calculatorData || {};

    return getNumber(
        calc?.materialDensityKgM3 ??
        calc?.stoneDensityKgM3 ??
        item?.materialDensityKgM3 ??
        item?.stoneDensityKgM3,
        0
    );
};

export const getOrderItemWorkType = (item = {}) => {
    const calc = item?.calculatorData || {};

    return getTextValue(
        calc?.workType,
        calc?.workKey,
        item?.workType,
        item?.workKey,
        calc?.serviceType,
        item?.serviceType
    );
};

export const getOrderItemWorkName = (item = {}) => {
    const calc = item?.calculatorData || {};
    const workType = getOrderItemWorkType(item);

    return getTextValue(
        calc?.workName,
        calc?.workLabel,
        item?.workName,
        item?.workLabel,
        workType === 'milling' ? 'Фрезеровка' : '',
        'Работа'
    );
};

export const getOrderItemType = (item = {}) => {
    const calc = item?.calculatorData || {};
    const rawOrderType = getTextValue(
        calc?.orderItemType,
        item?.orderItemType,
        calc?.cartItemType,
        item?.cartItemType,
        calc?.orderItemKind,
        item?.orderItemKind
    );
    const workType = getOrderItemWorkType(item);
    const measureMode = getTextValue(calc?.measureMode, item?.measureMode);

    const isWork =
        rawOrderType === 'work' ||
        rawOrderType === 'service' ||
        rawOrderType === 'milling' ||
        rawOrderType === 'engraving' ||
        rawOrderType === 'photoCeramic' ||
        workType === 'milling' ||
        workType === 'engraving' ||
        workType === 'photoCeramic' ||
        measureMode === 'milling';

    return isWork ? 'work' : 'product';
};

export const isWorkOrderItem = (item = {}) => {
    return getOrderItemType(item) === 'work';
};

export const getOrderItemTypeLabel = (item = {}) => {
    const calc = item?.calculatorData || {};
    const categoryLabel = getOrderItemCategoryLabel(item);
    const isWork = isWorkOrderItem(item);
    const workName = getOrderItemWorkName(item);

    return getTextValue(
        calc?.orderItemTypeLabel,
        item?.orderItemTypeLabel,
        isWork ? `Работа: ${workName}` : '',
        calc?.wholeItemLabel,
        item?.wholeItemLabel,
        categoryLabel ? `${categoryLabel}: изделие целиком` : 'Изделие целиком'
    );
};

export const getOrderItemProductPrice = (item = {}) => {
    const calc = item?.calculatorData || {};

    return getNumber(
        calc?.productPrice ??
        calc?.productBasePrice ??
        item?.productPrice ??
        item?.productBasePrice,
        0
    );
};

export const getOrderItemWorkPrice = (item = {}) => {
    const calc = item?.calculatorData || {};

    return getNumber(
        calc?.workPrice ??
        calc?.millingPrice ??
        item?.workPrice ??
        item?.millingPrice,
        0
    );
};

export const getOrderItemPrice = (item = {}) => {
    const calc = item?.calculatorData || {};
    const product = getProductObject(item);
    const isWork = isWorkOrderItem(item);
    const productPrice = getOrderItemProductPrice(item);
    const workPrice = getOrderItemWorkPrice(item);

    return getNumber(
        calc?.payablePrice ??
        calc?.selectedPrice ??
        item?.payablePrice ??
        item?.selectedPrice ??
        item?.price ??
        (isWork ? workPrice : productPrice) ??
        product?.price ??
        item?.bayerPrice,
        0
    );
};

export const getOrderItemTotal = (item = {}) => {
    return getOrderItemPrice(item) * getOrderItemQuantity(item);
};

export const getOrderItemVariantKey = (item = {}) => {
    const calc = item?.calculatorData || {};
    const productId = getProductIdFromItem(item);
    const type = getOrderItemType(item);
    const workType = getOrderItemWorkType(item) || 'whole';
    const sizeKey = getOrderItemSelectedSizeKey(item) || getOrderItemSizeLabel(item) || 'base';

    return getTextValue(
        calc?.variantKey,
        item?.variantKey,
        productId ? `${productId}::${type}::${workType}::${sizeKey}` : ''
    );
};

export const normalizeCartItemMeta = (product = {}, calculatorData = {}) => {
    const source = {
        ...product,
        calculatorData: {
            ...(product?.calculatorData || {}),
            ...(calculatorData || {}),
        },
    };

    const orderItemType = getOrderItemType(source);
    const workType = getOrderItemWorkType(source);
    const workName = getOrderItemWorkName(source);
    const categoryLabel = getOrderItemCategoryLabel(source);
    const sizeLabel = getOrderItemSizeLabel(source);
    const weightKg = getOrderItemWeightKg(source);
    const materialDensityKgM3 = getOrderItemMaterialDensity(source);
    const productPrice = getOrderItemProductPrice(source);
    const workPrice = getOrderItemWorkPrice(source);
    const payablePrice = getOrderItemPrice(source);

    return {
        orderItemType,
        workType,
        workKey: getTextValue(calculatorData?.workKey, product?.workKey, workType),
        workName,
        workLabel: workName,
        cartItemType: orderItemType,
        orderItemKind: calculatorData?.orderItemKind || product?.orderItemKind || (orderItemType === 'work' ? (workType || 'work') : 'wholeProduct'),
        orderItemTypeLabel: getOrderItemTypeLabel(source),
        categoryName: getTextValue(calculatorData?.categoryName, product?.categoryName),
        categoryLabel,
        sizeLabel,
        selectedSizeKey: getOrderItemSelectedSizeKey(source),
        selectedSizeLabel: sizeLabel,
        variantKey: getOrderItemVariantKey(source),
        measureMode: calculatorData?.measureMode || product?.measureMode || (workType === 'milling' ? 'milling' : 'piece'),
        weightKg,
        materialDensityKgM3,
        stoneDensityKgM3: materialDensityKgM3,
        productPrice,
        productBasePrice: productPrice,
        workPrice,
        millingPrice: workPrice,
        selectedPrice: payablePrice,
        payablePrice,
        price: payablePrice,
        millingDimensionsLabel: getTextValue(calculatorData?.millingDimensionsLabel, calculatorData?.sizeLabel, product?.millingDimensionsLabel),
        millingPriceLabel: getTextValue(calculatorData?.millingPriceLabel, calculatorData?.millingPriceSourceLabel, product?.millingPriceLabel),
    };
};

export const buildOrderItemPayload = (item = {}) => {
    const product = getProductObject(item);
    const productId = getProductIdFromItem(item);
    const qty = getOrderItemQuantity(item);
    const isWork = isWorkOrderItem(item);
    const workType = getOrderItemWorkType(item);
    const workName = getOrderItemWorkName(item);
    const sizeLabel = getOrderItemSizeLabel(item);
    const selectedSizeKey = getOrderItemSelectedSizeKey(item);
    const weightKg = getOrderItemWeightKg(item);
    const materialDensityKgM3 = getOrderItemMaterialDensity(item);
    const productPrice = getOrderItemProductPrice(item);
    const workPrice = getOrderItemWorkPrice(item);
    const payablePrice = getOrderItemPrice(item);
    const calculatorData = {
        ...(item?.calculatorData || {}),
        configured: item?.calculatorData?.configured === true || Boolean(sizeLabel || selectedSizeKey || workType),
        orderItemType: isWork ? 'work' : 'product',
        workType,
        workKey: getTextValue(item?.calculatorData?.workKey, item?.workKey, workType),
        workName,
        workLabel: workName,
        orderItemTypeLabel: getOrderItemTypeLabel(item),
        categoryName: getTextValue(item?.calculatorData?.categoryName, item?.categoryName, product?.categoryName),
        categoryLabel: getOrderItemCategoryLabel(item),
        sizeLabel,
        selectedSizeLabel: sizeLabel,
        selectedSizeKey,
        variantKey: getOrderItemVariantKey(item),
        weightKg,
        materialDensityKgM3,
        stoneDensityKgM3: materialDensityKgM3,
        productPrice,
        productBasePrice: productPrice,
        workPrice,
        millingPrice: workPrice,
        selectedPrice: payablePrice,
        payablePrice,
        modelName: getOrderItemName(item),
        mainImage: getOrderItemImage(item),
    };

    return {
        productId,
        product: productId,
        purchasedQty: qty,
        quantity: qty,
        qty,
        orderItemType: isWork ? 'work' : 'product',
        cartItemType: isWork ? 'work' : 'product',
        orderItemKind: calculatorData.orderItemKind || (isWork ? (workType || 'work') : 'wholeProduct'),
        orderItemTypeLabel: calculatorData.orderItemTypeLabel,
        workType,
        workKey: calculatorData.workKey,
        workName,
        workLabel: workName,
        categoryName: calculatorData.categoryName,
        categoryLabel: calculatorData.categoryLabel,
        sizeLabel,
        selectedSizeLabel: sizeLabel,
        selectedSizeKey,
        variantKey: calculatorData.variantKey,
        weightKg,
        materialDensityKgM3,
        stoneDensityKgM3: materialDensityKgM3,
        productPrice,
        productBasePrice: productPrice,
        workPrice,
        millingPrice: workPrice,
        selectedPrice: payablePrice,
        payablePrice,
        price: payablePrice,
        itemTotal: payablePrice * qty,
        calculatorData,
    };
};

export const buildOrderSummary = (items = []) => {
    const rows = asArray(items);
    const totalQty = rows.reduce((sum, item) => sum + getOrderItemQuantity(item), 0);
    const totalAmount = rows.reduce((sum, item) => sum + getOrderItemTotal(item), 0);
    const totalWeightKg = rows.reduce((sum, item) => {
        const weight = getOrderItemWeightKg(item);
        const qty = getOrderItemQuantity(item);

        return sum + (weight > 0 ? weight * qty : 0);
    }, 0);
    const unknownPriceItems = rows.filter((item) => getOrderItemPrice(item) <= 0).length;

    return {
        totalPositions: rows.length,
        totalQty,
        totalAmount,
        totalWeightKg,
        unknownPriceItems,
        hasKnownTotal: totalAmount > 0 && unknownPriceItems === 0,
    };
};

export const buildCartItemsHash = (cartItems = []) => {
    const items = asArray(cartItems)
        .map((item) => ({
            id: getOrderItemVariantKey(item) || getCartItemId(item) || getProductIdFromItem(item),
            qty: getOrderItemQuantity(item),
        }))
        .filter((item) => item.id);

    items.sort((a, b) => (a.id > b.id ? 1 : -1));

    return items.map((item) => `${item.id}:${item.qty}`).join('|');
};

export const normalizeOrderPayload = (payload = {}) => {
    const items = asArray(payload?.items).map(buildOrderItemPayload).filter((item) => item.productId);
    const summary = buildOrderSummary(items);

    return {
        ...payload,
        items,
        totalAmount: Number(payload?.totalAmount || 0) > 0 ? payload.totalAmount : summary.totalAmount,
        totalWeightKg: summary.totalWeightKg,
        totalPositions: summary.totalPositions,
        totalQty: summary.totalQty,
    };
};
